import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { GridColumns } from "@mui/x-data-grid/models/colDef/gridColDef";
import { Avatar, Typography, Button } from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import {
  DataGrid,
  GridRenderCellParams,
  GridFilterItem,
} from "@mui/x-data-grid";

import UserService from "../../../services/User.service";
import { useGlobalStyles } from "../../../util/ThemePalette";
import { defaultPageSize } from "../../../util/constants";
import { IStore } from "../../../../index";
import ModuleHelper from "../../../util/ModuleHelper";
import PaperWrapper from "../../paper/PaperWrapper";
import CustomHeader from "../../custom_grid_header/CustomHeader";
import {
  IAdvisorStatistic,
  ISellerStatistic,
} from "../../../models/statistics";

interface IProps {
  userService: UserService;
}

const SellerStatistics = (props: IProps): React.ReactElement => {
  const classes = useGlobalStyles();

  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pending, setPending] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<ISellerStatistic[]>([]);
  const [filter, setFilter] = useState<GridFilterItem[]>([]);
  const [showResetFilter, setShowResetFilter] = useState<boolean>(false);

  const dealerContext = useSelector(
    (store: IStore) => store.auth.user.dealerContext,
    shallowEqual
  );

  useEffect(() => {
    setPending(true);
    props.userService
      .getSellerStatistics()
      .then((response) => {
        setStatistics(response);
      })
      .catch((error) => ModuleHelper.grow.showError(error))
      .finally(() => setPending(false));
  }, [dealerContext]);

  const moneyCellRender = (
    params: GridRenderCellParams
  ): React.ReactElement => (
    <React.Fragment>
      <AttachMoneyIcon sx={{ fontSize: 15 }} color={"primary"} />
      <Typography>{params.value}</Typography>
    </React.Fragment>
  );

  const handleFilterChange = (e: any) => {
    setFilter(e.items);
    e.items[0].value ? setShowResetFilter(true) : setShowResetFilter(false);
  };

  const handleFilterReset = (e: any): void => {
    e.stopPropagation();
    setFilter([]);
    setShowResetFilter(false);
  };

  const columns: GridColumns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams<IAdvisorStatistic>) => {
        return (
          <section style={{ display: "flex" }}>
            <Avatar
              src={`${params.row?.employee.avatarURL}`}
              alt={params.row?.employee.firstName}
            />
            <Typography
              style={{ marginTop: 12, marginLeft: 5, whiteSpace: "initial" }}
            >
              {params.row?.employee.firstName} {params.row?.employee.lastName}
            </Typography>
          </section>
        );
      },
      headerAlign: "center",
      valueGetter: (e) =>
        e.row.employee.firstName + " " + e.row.employee.lastName,
      sortComparator: (v1, v2) => v1.localeCompare(v2),
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
    {
      field: "averageScore",
      headerName: "Average Score",
      minWidth: 150,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
    {
      field: "totalTipAmount",
      headerName: "Total Tip Amount",
      minWidth: 180,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: moneyCellRender,
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
  ];

  const render = (): React.ReactElement => {
    return (
      <PaperWrapper
        header={"SALES PERSON"}
        rightHeader={
          <Button
            variant={"contained"}
            startIcon={<FilterAltOffIcon />}
            onClick={handleFilterReset}
            style={{ display: showResetFilter ? "flex" : "none" }}
          >
            Reset
          </Button>
        }
      >
        <DataGrid
          rows={statistics}
          columns={columns}
          loading={pending}
          autoHeight={true}
          pageSize={pageSize}
          className={classes.grid}
          onPageSizeChange={setPageSize}
          onFilterModelChange={handleFilterChange}
          filterModel={{ items: filter }}
        />
      </PaperWrapper>
    );
  };

  return render();
};

export default SellerStatistics;
